import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Save, Plus, X, Target, ChevronLeft } from "lucide-react";

const TIMEFRAMES = ["This year", "3 years", "5 years", "10 years", "Lifetime"];

export default function Compass() {
  const navigate = useNavigate();
  const { current } = useWorkspace();
  const [compass, setCompass] = useState(null);
  const [mission, setMission] = useState("");
  const [principles, setPrinciples] = useState("");
  const [values, setValues] = useState([]);
  const [valueInput, setValueInput] = useState("");
  const [goals, setGoals] = useState([]);
  const [dirty, setDirty] = useState(false);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/compass`);
      setCompass(data);
      setMission(data.mission || "");
      setPrinciples(data.principles || "");
      setValues(data.values || []);
      setGoals(data.goals || []);
    } catch {}
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!wsId) return;
    try {
      await api.put(`/workspaces/${wsId}/compass`, { mission, principles, values, goals });
      setDirty(false);
      toast.success("Compass saved");
    } catch { toast.error("Failed to save"); }
  };

  const mark = () => setDirty(true);

  const addValue = () => {
    if (!valueInput.trim()) return;
    setValues(v => [...v, valueInput.trim()]);
    setValueInput(""); mark();
  };

  const addGoal = () => {
    setGoals(g => [...g, { goal_id: String(Date.now()), title: "", timeframe: "This year", description: "" }]);
    mark();
  };

  const updateGoal = (id, field, val) => { setGoals(g => g.map(x => x.goal_id === id ? {...x, [field]: val} : x)); mark(); };
  const removeGoal = (id) => { setGoals(g => g.filter(x => x.goal_id !== id)); mark(); };

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1.5 -ml-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex-shrink-0" title="Back to Overview">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-xl bg-[#5A90F5]/15 flex items-center justify-center"><Target className="w-4 h-4 text-[#5A90F5]"/></div>
          <div>
            <h1 className="text-[18px] font-bold text-[#EDEDED]">Compass</h1>
            <p className="text-[11px] text-[#3A3A3A]">Your direction and anchors</p>
          </div>
        </div>
        <button onClick={save} disabled={!dirty}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold transition-colors disabled:opacity-30"
          style={{ background: dirty ? "#5A90F5" : "#1A1A1A", color: dirty ? "#fff" : "#52525B" }}>
          <Save className="w-3.5 h-3.5"/> Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6 max-w-2xl space-y-8">
        {/* Mission */}
        <section>
          <h2 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider mb-3">Mission</h2>
          <p className="text-[11px] text-[#3A3A3A] mb-2">What are you ultimately trying to accomplish?</p>
          <textarea value={mission} onChange={e => { setMission(e.target.value); mark(); }}
            placeholder="Write your mission statement..."
            className="w-full bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 text-[14px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none focus:border-[#5A90F5]/50 transition-colors leading-relaxed"
            rows={3} />
        </section>

        {/* Values */}
        <section>
          <h2 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider mb-3">Values</h2>
          <p className="text-[11px] text-[#3A3A3A] mb-3">The principles that guide your decisions.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {values.map((v, i) => (
              <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#2A2A2A] rounded-xl text-[12px] text-[#EDEDED]">
                {v}
                <button onClick={() => { setValues(vals => vals.filter((_,j)=>j!==i)); mark(); }} className="text-[#3A3A3A] hover:text-[#EF4444]"><X className="w-3 h-3"/></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={valueInput} onChange={e=>setValueInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addValue()}}
              placeholder="Add a value..."
              className="flex-1 bg-[#111] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5]/50"/>
            <button onClick={addValue} className="px-3 py-2 rounded-xl bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#52525B] hover:text-[#EDEDED] transition-colors"><Plus className="w-3.5 h-3.5"/></button>
          </div>
        </section>

        {/* Goals */}
        <section>
          <h2 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider mb-3">Goals</h2>
          <p className="text-[11px] text-[#3A3A3A] mb-3">Long-range anchors that give your work meaning.</p>
          <div className="space-y-3 mb-3">
            {goals.map(g => (
              <div key={g.goal_id} className="p-4 bg-[#111] border border-[#2A2A2A] rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <input value={g.title} onChange={e=>updateGoal(g.goal_id,"title",e.target.value)}
                      placeholder="Goal title..."
                      className="w-full bg-transparent text-[13px] font-semibold text-[#EDEDED] placeholder-[#3A3A3A] outline-none border-b border-[#2A2A2A] pb-1.5 focus:border-[#5A90F5]/50"/>
                    <div className="flex items-center gap-3">
                      <select value={g.timeframe} onChange={e=>updateGoal(g.goal_id,"timeframe",e.target.value)}
                        className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg px-2 py-1 text-[10px] text-[#52525B] outline-none">
                        {TIMEFRAMES.map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <input value={g.description||""} onChange={e=>updateGoal(g.goal_id,"description",e.target.value)}
                      placeholder="Why does this matter?"
                      className="w-full bg-transparent text-[11px] text-[#52525B] placeholder-[#3A3A3A] outline-none"/>
                  </div>
                  <button onClick={()=>removeGoal(g.goal_id)} className="text-[#3A3A3A] hover:text-[#EF4444] mt-0.5"><X className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={addGoal} className="w-full py-3 rounded-xl border border-dashed border-[#2A2A2A] text-[11px] text-[#3A3A3A] hover:text-[#52525B] hover:border-[#3A3A3A] flex items-center justify-center gap-2 transition-colors">
            <Plus className="w-3.5 h-3.5"/> Add goal
          </button>
        </section>

        {/* Principles */}
        <section>
          <h2 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider mb-3">Principles</h2>
          <p className="text-[11px] text-[#3A3A3A] mb-2">How you operate day-to-day.</p>
          <textarea value={principles} onChange={e=>{setPrinciples(e.target.value);mark()}}
            placeholder="Your working principles..."
            className="w-full bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none focus:border-[#5A90F5]/50 leading-relaxed"
            rows={4}/>
        </section>
      </div>
    </div>
  );
}
